import { useMemo } from 'react';

import VerifiedIcon from '@mui/icons-material/Verified';
import { Grid, Tooltip } from '@mui/material';
import { type GridColDef, type GridRowsProp } from '@mui/x-data-grid-premium';

import {
  type IntlLocale,
  type Translate,
  useLocale,
} from '../../../src/hooks/locale';
import { type Currency, getCurrencyByCountryCode } from '../../../src/locale';
import { formatPrice } from '../../../src/utils/format-price';
import { gql } from '../../__generated__';
import type {
  GetPropertyTransactionsQuery,
  TransactionsListRowFragment,
} from '../../__generated__/graphql';
import { useAppData } from '../../providers/AppDataProvider';
import { formatDateNumeric } from '../../utils/formatting';
import { PropertyCell } from '../data-grid/PropertyCell';
import { RaDataGrid, type RaDataGridProps } from '../data-grid/RaDataGrid';
import { TeamInfo } from '../data-grid/TeamInfo';
import { UserInfo } from '../data-grid/UserInfo';

type TransactionsListProps = {
  transactions: TransactionsListRowFragment[] | undefined;
  loading: boolean;
  extraDataGridProps?: Omit<RaDataGridProps, 'loading' | 'rows' | 'columns'>;
  displayPropertyInfo?: boolean;
  showReviews?: boolean;
};

export const TRANSATIONS_LIST_ROW_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionsListRow on property_transactions {
    id
    signature_date
    estimated_signature_date
    payment_date
    purchase_price
    seller_broker {
      id
      ...UserInfo_user
    }
    buyer_broker {
      id
      ...UserInfo_user
    }
    seller_broker_team {
      id
      ...TeamInfo_team
    }
    buyer_broker_team {
      id
      ...TeamInfo_team
    }
    property {
      ...PropertyCell_property
      living_surface
      built_surface
      land_surface
      number_of_rooms
      number_of_bedrooms
      property_type {
        id
        label
      }
    }
    commissions {
      id
      is_paid
      amount
    }
    lot {
      id
      currency
    }
    sellers(limit: 1) {
      user {
        id
        ...UserInfo_user
      }
    }
    buyers(limit: 1) {
      user {
        id
        ...UserInfo_user
      }
    }
    commissions_aggregate(where: { type: { _eq: "total" } }) {
      aggregate {
        sum {
          amount
        }
      }
    }
    my_commissions: commissions_aggregate(
      where: { type: { _eq: "split" }, beneficiary_team_id: { _in: $team_ids } }
    ) {
      aggregate {
        sum {
          amount
        }
      }
    }
  }
`);

const formatAmountWithLotCurrency = (
  amount: number | null | undefined,
  locale: IntlLocale,
  transaction: TransactionsListRowFragment,
) => {
  const currency = (transaction.lot?.currency ??
    getCurrencyByCountryCode(
      transaction.property.country_code ?? 'CH',
    )) as Currency;

  return formatPrice(amount, locale, currency);
};

const getColumns = (
  t: Translate,
  locale: IntlLocale,
  displayPropertyInfo: boolean,
  tenantCountryCode: string | undefined,
  showReviews: boolean,
): GridColDef<TransactionsListRowFragment>[] => {
  const columns: GridColDef<TransactionsListRowFragment>[] = [];
  const defaultCurrency = getCurrencyByCountryCode(tenantCountryCode ?? 'CH');

  if (displayPropertyInfo) {
    columns.push(
      {
        field:
          'property.postcode,property.locality,property.route,property.street_number',
        headerName: 'Listing',
        width: 200,
        sortable: false,
        renderCell: ({ row, id }) =>
          id !== 'auto-generated-group-footer-root' && (
            <PropertyCell property={row.property} />
          ),
      },
      {
        field: 'property',
        headerName: 'Property Information',
        width: 190,
        display: 'flex',
        sortable: false,
        renderCell: ({ row, id }) =>
          id !== 'auto-generated-group-footer-root' && (
            <div>
              <div>
                {row.property.property_type?.label ?? <i>{t('No type')}</i>}
              </div>
              <div>
                {(row.property.country_code ?? tenantCountryCode) === 'ES'
                  ? t(
                      '{{number_of_bedrooms}} bedrooms ﹒ {{built_surface}} m²',
                      {
                        number_of_bedrooms:
                          row.property?.number_of_bedrooms ?? '-',
                        built_surface: row.property?.built_surface ?? '-',
                        count: row.property?.number_of_bedrooms ?? 0,
                      },
                    )
                  : t('{{number_of_rooms}} rooms ﹒ {{living_surface}} m²', {
                      number_of_rooms: row.property?.number_of_rooms ?? '-',
                      living_surface: row.property?.living_surface ?? '-',
                      count: row.property?.number_of_rooms ?? 0,
                    })}
              </div>
            </div>
          ),
      },
    );
  }

  columns.push(
    {
      field: 'purchase_price',
      headerName: t('Price'),
      width: 140,
      display: 'flex',
      align: 'right',
      type: 'number',
      renderCell: ({ row, value, id }) =>
        id === 'auto-generated-group-footer-root'
          ? formatPrice(value, locale, defaultCurrency, 0, '-')
          : formatAmountWithLotCurrency(value, locale, row),
    },
    {
      field: 'seller_broker.last_name,seller_broker.first_name',
      headerName: t('Seller agent'),
      display: 'flex',
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.seller_broker && <UserInfo user={row.seller_broker} />,
    },
    {
      field: 'seller_broker_team.name',
      headerName: t('Seller agent team'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.seller_broker_team && <TeamInfo team={row.seller_broker_team} />,
    },
    {
      field: 'buyer_broker.last_name,buyer_broker.first_name',
      headerName: t('Buyer agent'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.buyer_broker && <UserInfo user={row.buyer_broker} />,
    },
    {
      field: 'buyer_broker_team.name',
      headerName: t('Buyer agent team'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.buyer_broker_team && <TeamInfo team={row.buyer_broker_team} />,
    },
    {
      field: 'buyers.user.last_name,buyers.user.first_name',
      headerName: t('Buyer'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.buyers != null &&
        row.buyers.length > 0 && <UserInfo user={row.buyers[0].user} />,
    },
    {
      field: 'sellers.user.last_name,sellers.user.first_name',
      headerName: t('Seller'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.sellers != null &&
        row.sellers.length > 0 && <UserInfo user={row.sellers[0].user} />,
    },
    {
      field: 'signature_date',
      headerName: 'Signature Date',
      width: 150,
      renderCell: ({ row }) => {
        if (row.signature_date) {
          return formatDateNumeric(new Date(row.signature_date), locale);
        } else if (row.estimated_signature_date) {
          return new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric',
          }).format(new Date(row.estimated_signature_date));
        }
        return '-';
      },
    },
    {
      field: 'payment_date',
      headerName: 'Payment Date',
      width: 120,
      renderCell: ({ row }) =>
        row.payment_date
          ? formatDateNumeric(new Date(row.payment_date), locale)
          : '-',
    },
  );

  if (showReviews) {
    columns.push({
      field: 'reviews',
      sortable: false,
      headerName: t('Reviews'),
      width: 80,
      display: 'flex',
      renderCell: ({ row, id }) => {
        if (id === 'auto-generated-group-footer-root') {
          return null;
        }

        const rowWithReviews =
          row as GetPropertyTransactionsQuery['property_transactions'][number];

        const hasBuyerReview = rowWithReviews.reviews.some(r => r.is_buyer);
        const hasSellerReview = rowWithReviews.reviews.some(r => !r.is_buyer);

        let tooltipText = t('No reviews');

        if (hasBuyerReview && hasSellerReview) {
          tooltipText = t('Buyer and seller reviews');
        } else if (hasBuyerReview) {
          tooltipText = t('Buyer review');
        } else if (hasSellerReview) {
          tooltipText = t('Seller review');
        }

        return (
          <Tooltip title={tooltipText}>
            <Grid container>
              <Grid item xs={6} sx={{ textAlign: 'center' }}>
                {hasSellerReview ? <VerifiedIcon color="success" /> : '-'}
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'center' }}>
                {hasBuyerReview ? <VerifiedIcon color="success" /> : '-'}
              </Grid>
            </Grid>
          </Tooltip>
        );
      },
    });
  }

  columns.push(
    {
      field: 'commissions_aggregate.sum',
      headerName: t('Rate'),
      width: 120,
      align: 'right',
      type: 'number',
      sortable: false,
      valueGetter: (_, row) =>
        row.purchase_price &&
        row.commissions_aggregate?.aggregate?.sum?.amount &&
        row.commissions_aggregate?.aggregate?.sum?.amount / row.purchase_price,
      valueFormatter: (value: number) =>
        value != null
          ? new Intl.NumberFormat(locale, {
              style: 'percent',
              maximumFractionDigits: 1,
            }).format(value)
          : '-',
    },
    {
      field: 'commissions_aggregate.sum.amount',
      headerName: t('Commission'),
      width: 120,
      align: 'right',
      type: 'number',
      display: 'flex',
      valueGetter: (_, row) =>
        row.commissions_aggregate?.aggregate?.sum?.amount ?? 0,
      renderCell: ({ row, value, id }) =>
        id === 'auto-generated-group-footer-root'
          ? formatPrice(value, locale, defaultCurrency, 0, '-')
          : formatAmountWithLotCurrency(value, locale, row),
    },
    {
      field: 'my_commissions.aggregate.sum.amount',
      headerName: t('My Share'),
      width: 120,
      align: 'right',
      type: 'number',
      sortable: false,
      valueGetter: (_, row) => row.my_commissions?.aggregate?.sum?.amount ?? 0,
      renderCell: ({ row, value, id }) =>
        id === 'auto-generated-group-footer-root'
          ? formatPrice(value, locale, defaultCurrency, 0, '-')
          : formatAmountWithLotCurrency(value, locale, row),
    },
  );

  return columns;
};

export const TransactionsList: React.FC<TransactionsListProps> = ({
  transactions,
  loading,
  displayPropertyInfo = false,
  extraDataGridProps,
  showReviews = false,
}) => {
  const { t, intlLocale } = useLocale();
  const { me } = useAppData();
  const columns = useMemo(
    () =>
      getColumns(
        t,
        intlLocale,
        displayPropertyInfo,
        me?.tenant.country_code,
        showReviews,
      ),
    [t, intlLocale, displayPropertyInfo, me?.tenant.country_code, showReviews],
  );

  const rows: GridRowsProp<TransactionsListRowFragment> = transactions ?? [];

  return (
    <RaDataGrid
      loading={loading}
      rows={rows}
      columns={columns}
      disableColumnSelector={false}
      disableColumnFilter
      {...extraDataGridProps}
    />
  );
};
